import * as styleSheetLib from "@client/lib/styleSheet";
import { getLayoutSettingsStructure } from "@client/settings/layout";
import { getItemSettingsStructure, getSettingsStructure } from "@client/settings/module";
import { type Entities, EntityType, type GenericMap, type MainSettings } from "@client/types";
import { type CompatibilitySnapshotData, type SnapshotData } from "@client/types/Snapshot";
import { type StyleSheetData, type StyleSheetTextType } from "@client/types/StyleSheet";
import { type Nullable, type TypedMap } from "@common/declarations";
import i18n from "@common/lib/i18n";
import { get, isEqual, isUndefined } from "lodash";
import { compareVersionNumbers } from "..";
import { isModuleEntity } from "../../layout";
import {
  addVersion,
  fromShorthand,
  getResponsiveValue,
  moveResponsiveValue,
  processPossiblyResponsiveValue,
  processSettings,
  setResponsiveValue,
  setValue,
  toShorthand
} from "../../settings";
import { STYLE_SHEET_IDS } from "../../styleSheet";
import { getEntitySettingsStructure } from "../040000/configs/lib";
import { processColorSettings, processStyleSheetSettings } from "./lib";

const getTextValue = (settings: MainSettings, prop: string): MainSettings => {
  let r = getResponsiveValue(settings, prop);
  r = setResponsiveValue(r, "color", undefined);
  r = setResponsiveValue(r, "linkColor", undefined);
  return r;
};

export type UpgradeOpts = {
  folderName?: string;
  forceStyleSheets: boolean;
};

export type StyleSheetIds = {
  colors: TypedMap<StyleSheetTextType, string> & {
    primary: string;
  };
  link: string;
  text: TypedMap<StyleSheetTextType, string>;
};
export type StyleSheetTextIds = TypedMap<StyleSheetTextType, string>;

export const UPGRADE_STYLE_SHEET_IDS: StyleSheetIds = {
  colors: {
    h1: "4u7oKuYRytkC",
    h2: "ro7V2JzT07V4",
    h3: "SwixFQPWy2wu",
    h4: "BIBj8Kxad4pp",
    h5: "1c_zxFt8sKj9",
    h6: "aZLBDhtJip59",
    text: STYLE_SHEET_IDS.defaults.textColor,
    primary: STYLE_SHEET_IDS.defaults.primaryColor
  },
  link: STYLE_SHEET_IDS.defaults.link,
  text: {
    h1: STYLE_SHEET_IDS.defaults.h1,
    h2: STYLE_SHEET_IDS.defaults.h2,
    h3: STYLE_SHEET_IDS.defaults.h3,
    h4: STYLE_SHEET_IDS.defaults.h4,
    h5: STYLE_SHEET_IDS.defaults.h5,
    h6: STYLE_SHEET_IDS.defaults.h6,
    text: STYLE_SHEET_IDS.defaults.text
  }
};

const getTextLabel = (type: StyleSheetTextType) => {
  switch (type) {
    case "h1":
      return "Heading 1";
    case "h2":
      return "Heading 2";
    case "h3":
      return "Heading 3";
    case "h4":
      return "Heading 4";
    case "h5":
      return "Heading 5";
    case "h6":
      return "Heading 6";
    case "text":
      return "Text";
  }
};

const VERSION = "05.00.00";
export const upgrade = (
  styleSheet: StyleSheetData,
  data: Nullable<CompatibilitySnapshotData>,
  forceStyleSheets: boolean
): Nullable<SnapshotData> => {
  const addedLinks: GenericMap<MainSettings> = {};

  let dataStyleSheet = data.styleSheet;
  if (!data.styleSheet && data.settings) {
    dataStyleSheet = createDefaultStyleSheet(data.settings, UPGRADE_STYLE_SHEET_IDS);
  }

  let definedStyleSheet = dataStyleSheet ?? styleSheet;

  if (!data.layout && data.settings) {
    data.layout = upgradeLayout(definedStyleSheet, data.settings, addedLinks, UPGRADE_STYLE_SHEET_IDS, {
      forceStyleSheets
    });
  }

  if (data.entities) {
    data.entities = upgradeEntities(definedStyleSheet, data.entities, addedLinks, UPGRADE_STYLE_SHEET_IDS, {
      forceStyleSheets
    });
  }

  return {
    entities: data.entities,
    layout: data.layout ?? null,
    styleSheet: dataStyleSheet ?? null
  };
};

export const createDefaultStyleSheet = (settings: MainSettings, styleSheetIds: StyleSheetIds): StyleSheetData => {
  let styleSheet: StyleSheetData = {
    settings: {},
    structure: [
      { id: "typography", label: i18n.typography },
      { id: "colors", label: i18n.colors },
      { id: "links", label: i18n.links }
    ],
    version: VERSION
  };

  styleSheet = moveLayoutToStyleSheet(styleSheet, settings, styleSheetIds, undefined);

  return styleSheet;
};

export const moveLayoutToStyleSheet = (
  styleSheet: StyleSheetData,
  settings: MainSettings,
  styleSheetIds: StyleSheetIds,
  opts?: UpgradeOpts
): StyleSheetData => {
  if (compareVersionNumbers(settings.version, VERSION) >= 0) return styleSheet;

  const typographyParentId = opts?.folderName
    ? styleSheetLib.upsertFolder(styleSheet.structure, "typography", opts.folderName)
    : "typography";
  const linksParentId = opts?.folderName
    ? styleSheetLib.upsertFolder(styleSheet.structure, "links", opts.folderName)
    : "links";
  const colorsParentId = opts?.folderName
    ? styleSheetLib.upsertFolder(styleSheet.structure, "colors", opts.folderName)
    : "colors";

  styleSheet = styleSheetLib.addItem(
    styleSheet,
    styleSheetIds.text.text,
    typographyParentId,
    "text",
    i18n.text,
    getTextValue(settings, "text")
  );
  styleSheet = styleSheetLib.addItem(
    styleSheet,
    styleSheetIds.text.h1,
    typographyParentId,
    "h1",
    i18n.heading1,
    getTextValue(settings, "h1")
  );
  styleSheet = styleSheetLib.addItem(
    styleSheet,
    styleSheetIds.text.h2,
    typographyParentId,
    "h2",
    i18n.heading2,
    getTextValue(settings, "h2")
  );
  styleSheet = styleSheetLib.addItem(
    styleSheet,
    styleSheetIds.text.h3,
    typographyParentId,
    "h3",
    i18n.heading3,
    getTextValue(settings, "h3")
  );
  styleSheet = styleSheetLib.addItem(
    styleSheet,
    styleSheetIds.text.h4,
    typographyParentId,
    "h4",
    i18n.heading4,
    getTextValue(settings, "h4")
  );
  styleSheet = styleSheetLib.addItem(
    styleSheet,
    styleSheetIds.text.h5,
    typographyParentId,
    "h5",
    i18n.heading5,
    getTextValue(settings, "h5")
  );
  styleSheet = styleSheetLib.addItem(
    styleSheet,
    styleSheetIds.text.h6,
    typographyParentId,
    "h6",
    i18n.heading6,
    getTextValue(settings, "h6")
  );

  styleSheet = styleSheetLib.addItem(
    styleSheet,
    styleSheetIds.colors.primary,
    colorsParentId,
    "color",
    i18n.primaryColor,
    processSettings(settings, (x, s) => {
      const scheme = x.colorScheme;
      const color = x.customColor;

      const res = scheme === "custom" ? color : scheme;
      if (isUndefined(res)) return;

      return {
        color: res
      };
    })
  );

  styleSheet = styleSheetLib.addItem(
    styleSheet,
    styleSheetIds.link,
    linksParentId,
    "link",
    i18n.link,
    processSettings(settings, (x, s) => {
      const color = get(x, "text.linkColor");
      if (isUndefined(color)) return;

      return {
        color,
        decoration: "none",
        hover: {
          color,
          decoration: "none"
        }
      };
    })
  );

  const getColor = (textType: StyleSheetTextType): any => getResponsiveValue(settings!, `${textType}.color`);
  const textColors: TypedMap<StyleSheetTextType, any> = {
    text: getColor("text"),
    h1: getColor("h1"),
    h2: getColor("h2"),
    h3: getColor("h3"),
    h4: getColor("h4"),
    h5: getColor("h5"),
    h6: getColor("h6")
  };

  const shouldAddHeadingColors = Object.values(textColors).some((x) => !isEqual(x, textColors.text));

  Object.keys(textColors).forEach((x: StyleSheetTextType) => {
    if (x !== "text" && !shouldAddHeadingColors) return;

    styleSheet = styleSheetLib.addItem(
      styleSheet,
      styleSheetIds.colors[x],
      colorsParentId,
      "color",
      getTextLabel(x),
      setResponsiveValue({}, "color", textColors[x])
    );
  });

  return styleSheet;
};

export const upgradeLayout = (
  styleSheet: StyleSheetData,
  settings: MainSettings,
  addedLinks: GenericMap<MainSettings>,
  styleSheetIds: StyleSheetIds,
  opts?: UpgradeOpts
): MainSettings => {
  if (compareVersionNumbers(settings.version, VERSION) >= 0) return settings;

  settings = processSettings(settings, (x, s) => {
    delete x.text;
    delete x.h1;
    delete x.h2;
    delete x.h3;
    delete x.h4;
    delete x.h5;
    delete x.h6;
    return x;
  });

  moveResponsiveValue(settings, "header.visible", "elements.header", (x) => x !== false);
  moveResponsiveValue(settings, "sidePanel.visible", "elements.sidePanel", (x) => x !== false);
  processPossiblyResponsiveValue(getResponsiveValue(settings, "floatingMenu"), (x, s) => {
    setValue(settings, "elements.floatingMenu", !!x.elements, s);
  });

  processPossiblyResponsiveValue(getResponsiveValue(settings, "header.padding"), (x, s) => {
    if (x) {
      const parsed = fromShorthand(x, "{top} {right} {bottom} {left}");

      if (!parsed.value.right || parsed.value.right.endsWith("px")) {
        parsed.value.right = `${parseFloat(parsed.value.right) + 15}px`;

        setValue(settings, "header.padding", toShorthand(parsed.value, "{top} {right} {bottom} {left}", "0px"), s);
      }
    }
  });

  const ls = getLayoutSettingsStructure();
  settings = processStyleSheetSettings(
    "layout",
    styleSheet,
    ls.structure,
    ls.definitions,
    settings,
    undefined,
    addedLinks,
    styleSheetIds,
    opts
  );

  return settings;
};

export const upgradeEntities = (
  styleSheet: StyleSheetData,
  entities: Entities,
  addedLinks: GenericMap<MainSettings>,
  styleSheetIds: StyleSheetIds,
  opts?: UpgradeOpts
): Entities => {
  Object.entries(entities).forEach((p) => {
    const id = p[0];
    const entity = p[1];
    if (!entity.id) {
      // somehow 1021 page contained such entities
      delete entities[id];
      return;
    }

    if (!entity.settings || compareVersionNumbers(entity.settings.version, VERSION) >= 0) return;

    if (entity.type === EntityType.ModuleItem) {
      const parent = entity.parentId ? entities[entity.parentId] : null;
      if (parent && isModuleEntity(parent) && parent.moduleType === "MiniProfile") {
        moveResponsiveValue(entity.settings, "text", "caption");
      }
    }

    if (isModuleEntity(entity)) {
      const mss = getSettingsStructure(entity.moduleType);
      // console.log(`${VERSION} ${entity.id}: ${entity.moduleType} module`);
      entity.settings = processStyleSheetSettings(
        `${entity.moduleType}Module`,
        styleSheet,
        mss.settingsStructure,
        mss.definitions,
        entity.settings,
        mss.commonStructure,
        addedLinks,
        styleSheetIds,
        opts
      );

      if (entity.moduleType === "TextHTML" && entity.settings) {
        processPossiblyResponsiveValue(getResponsiveValue(entity.settings, "description"), (x, s) => {
          if (!x) return;

          entity.settings = setValue(entity.settings!, "description", x.replaceAll(/<p>[\s&#xa0]+<\/p>/gm, ""), s);
        });
      }
    } else if (entity.type === EntityType.ModuleItem) {
      if (!entity.parentId) return;
      const parent = entities[entity.parentId];
      if (!isModuleEntity(parent)) return;

      const mss = getSettingsStructure(parent.moduleType);

      // console.log(`${VERSION} ${entity.id}: ${parent.moduleType} module item`);
      entity.settings = processStyleSheetSettings(
        `${entity.data?.type ?? "default"}${parent.moduleType}ModuleItem`,
        styleSheet,
        getItemSettingsStructure(parent.moduleType, entity.data?.type ?? "default"),
        mss.definitions,
        entity.settings,
        mss.commonStructure,
        addedLinks,
        styleSheetIds,
        opts
      );
    } else {
      const ess = getEntitySettingsStructure(entity.type);
      entity.settings = processColorSettings(
        entity.type,
        styleSheet,
        ess.structure,
        ess.definitions,
        entity.settings,
        {},
        addedLinks,
        styleSheetIds,
        opts
      );
    }

    entity.settings = addVersion(entity.settings, VERSION);
  });

  return entities;
};
